/**
 * Device condition node definition.
 * Converts device condition/trigger to scene JSON.
 */

import type { NodeDefinition, SceneJsonContext } from '../types';
import type { FlowNode } from '../../../types/flow-types';
import { isDeviceCheckSingleOutput } from '../../../helpers/deviceCheckMode';
import {
	createDeviceTriggerFromFlow,
	createDeviceConditionFromFlow,
} from '../../../converters/helpers/device-conditions';

/** Palette entries that use execution mode (once / any / repeat) in the inspector. */
export function isDeviceCheckPalette(node: FlowNode): boolean {
	const pk = String((node.data as any)?.paletteKey || '');
	return pk === 'deviceCheck' || pk === 'guardCondition';
}

export { isDeviceCheckSingleOutput };

function getConditionDeviceData(node: FlowNode): { id: string; channel: string } | null {
	const dc = node.data._device_condition as any;
	const cap = node.data.sceneCapability as any;
	if (node.data.device?.id) {
		return {
			id: node.data.device.id,
			channel: String(node.data.device.channel ?? '0'),
		};
	}
	if (dc?.device?.id) {
		return {
			id: dc.device.id,
			channel: String(dc.device.channel ?? '0'),
		};
	}
	if (cap?.deviceId) {
		return {
			id: cap.deviceId,
			channel: String(cap.channel ?? '0'),
		};
	}
	return null;
}

function getConditionPropOpVal(node: FlowNode): { property: string; operator: string; value: any } {
	const dc = node.data._device_condition as any;
	const cap = node.data.sceneCapability as any;
	const property =
		node.data.cond_name ?? dc?.cond_name ?? cap?.capabilityName ?? node.data.property?.id ?? 'state';
	const operator = node.data.operator ?? dc?.operator ?? cap?.operator ?? node.data.cmpop ?? 'eq';
	const value = node.data.value ?? dc?.value ?? cap?.value ?? node.data.cmpval ?? 0;
	return { property, operator, value };
}

export const deviceConditionDefinition: NodeDefinition = {
	type: 'condition',
	category: 'condition',
	sourceHandles: ['true', 'false'],

	defaultData: () => ({
		label: 'Condition',
		triggerType: 'device',
	}),

	toSceneJson: (node: FlowNode, context?: SceneJsonContext) => {
		// Only fall back to cached CBL when there is no structured device data.
		// When device/property/operator/value are available, always re-serialize
		// from those fields so inspector edits are reflected in the output.
		const dc = node.data._device_condition as any;
		const cap = node.data.sceneCapability as any;
		const hasStructuredData = !!(node.data.device?.id || dc?.device?.id || cap?.deviceId);

		if (!hasStructuredData) {
			if (node.data.sceneData) {
				const sceneData = { ...(node.data.sceneData as any) };
				sceneData._fid = sceneData._fid || node.id;
				return sceneData;
			}
			if (node.data.originalCondition) {
				const original = { ...(node.data.originalCondition as any) };
				original._fid = original._fid || node.id;
				return original;
			}
			return null;
		}

		const device = getConditionDeviceData(node);
		if (!device) return null;

		const { property, operator, value } = getConditionPropOpVal(node);

		// Nested conditions (inside do/else branches) must evaluate synchronously
		// when the parent fires, not as independent reactive subscriptions.
		// The standalone r/cmpop/cmpval form is the only correct choice here.
		if (context?.nested) {
			return createDeviceConditionFromFlow({
				deviceId: device.id,
				channel: device.channel,
				property,
				operator,
				value,
			});
		}

		// Default Device Condition: always expression trigger "any change" at top level.
		if (!isDeviceCheckPalette(node)) {
			return createDeviceTriggerFromFlow(
				{ deviceId: device.id, channel: device.channel, property, operator, value },
				'any',
			);
		}

		const executionMode = node.data.executionMode ?? node.data.cond_type?.id;

		if (executionMode === 'once' || executionMode === 1) {
			return createDeviceTriggerFromFlow(
				{ deviceId: device.id, channel: device.channel, property, operator, value },
				'once',
				node.data.hold_off ?? node.data.conditionType?.valid_for_min ?? 5,
			);
		}
		if (executionMode === 'repeat' || executionMode === 3) {
			return createDeviceTriggerFromFlow(
				{ deviceId: device.id, channel: device.channel, property, operator, value },
				'repeat',
			);
		}
		if (executionMode === 'any' || executionMode === 2) {
			return createDeviceTriggerFromFlow(
				{ deviceId: device.id, channel: device.channel, property, operator, value },
				'any',
			);
		}

		return createDeviceConditionFromFlow({
			deviceId: device.id,
			channel: device.channel,
			property,
			operator,
			value,
		});
	},
};
